import { InhibitRestartStatus } from 'app/pages/InhibitRestartPage/type';
import { useMemo } from 'react';

type NotificationType =
  | 'feature-available-in-premium'
  | 'you-do-not-have-access'
  | null;

export interface ToggleState {
  isChecked: boolean;
  isDisabled: boolean;
  notificationType: NotificationType;
}

export interface Props {
  hasAccess: boolean;
  canModify?: boolean;
  isSubscriptionsFlagEnabled: boolean;
  hasPremiumSubscription: boolean;
  equipmentFeatureStatus?: string;
  equipmentStatus?: string;
  commandStatus?: string;
}

export default function useToggleLogic(props: Props): ToggleState {
  const {
    hasAccess = false,
    canModify = false,
    isSubscriptionsFlagEnabled,
    hasPremiumSubscription,
    equipmentFeatureStatus = '',
    equipmentStatus = '',
    commandStatus = '',
  } = props;

  const isDisabled = useMemo<boolean>(() => {
    if (!canModify) {
      return true;
    }

    if (
      isSubscriptionsFlagEnabled &&
      !hasPremiumSubscription &&
      equipmentFeatureStatus === InhibitRestartStatus.Disabled
    ) {
      return true;
    }

    return false;
  }, [
    canModify,
    isSubscriptionsFlagEnabled,
    hasPremiumSubscription,
    equipmentFeatureStatus,
  ]);

  const isChecked = useMemo(() => {
    const isFeatureActive =
      equipmentFeatureStatus === InhibitRestartStatus.Enabled &&
      equipmentStatus === InhibitRestartStatus.RestartDisabled;

    const isForcefullyActive = !canModify && isFeatureActive;
    const isEnabledAndActive = canModify && isFeatureActive;

    return isForcefullyActive || isEnabledAndActive;
  }, [
    canModify,
    isSubscriptionsFlagEnabled,
    equipmentFeatureStatus,
    equipmentStatus,
  ]);

  const notificationType = useMemo<NotificationType>(() => {
    if (!canModify || !hasAccess) {
      return 'you-do-not-have-access';
    }

    if (isSubscriptionsFlagEnabled && !hasPremiumSubscription) {
      const isCondition1 =
        equipmentFeatureStatus === InhibitRestartStatus.Disabled;

      const isCondition2 =
        equipmentFeatureStatus === InhibitRestartStatus.Enabled &&
        equipmentStatus === InhibitRestartStatus.RestartEnabled &&
        commandStatus === InhibitRestartStatus.RestartEnabled;

      if (isCondition1 || isCondition2) {
        return 'feature-available-in-premium';
      }
    }

    return null;
  }, [
    hasAccess,
    canModify,
    isSubscriptionsFlagEnabled,
    hasPremiumSubscription,
    equipmentFeatureStatus,
    equipmentStatus,
    commandStatus,
  ]);

  return {
    isChecked,
    isDisabled,
    notificationType,
  };
}
